import { MarketScopeType, RequestStatus, StakeholderNewsWatchFilterLevel, defaults, entities } from 'typed/api';
import { Action } from 'typed/actions';
import {
    IReducer, childrenReducer, combineReducers, defaultReducer, entityAddAllReducer, entityAddReducer,
    entityDeleteAllReducer, entityDeleteReducer, entityEditAllReducer, entityEditReducer, entityLoadAllReducer, entityLoadReducer, entityVoidAllReducer, nestableEntitiesReducer,
    networkObjectLoadReducer, sortedEntitiesReducer, switchReducers, synchronizableEnumReducer
} from './utils';
import { deepAssign, deepClone } from 'typed/utils';
import { PersistentFinancialInformationKey } from 'utils/constants';

// TODO find a way to infer this
const
    ws = defaults.NetworkArray,
    wp = defaults.NetworkPaginationSlice,
    t = defaults.StakeholderTypeReport,
    ts = defaults.NetworkArrayOf(t),
    s = defaults.StakeholderReport,
    ss = defaults.NetworkPaginationSliceOf(s),
    c = defaults.StakeholderComment,
    cs = defaults.NetworkPaginationSliceOf(c),
    is = defaults.NetworkArrayOf(defaults.FinancialIndicator),
    vts = defaults.NetworkArrayOf(defaults.StakeholderMarketPositioningMapViewTemplate),
    v = defaults.StakeholderMarketPositioningMapViewReport,
    vs = defaults.NetworkArrayOf(v),
    ps = defaults.NetworkPaginationSliceOf(defaults.StakeholderMarketPositioningMapViewPosition),
    vis = defaults.NetworkPaginationSliceOf(defaults.StakeholderMarketPositioningMapViewCustomItem),
    bts = defaults.NetworkArrayOf(defaults.StakeholderBenchmarkTemplate),
    b = defaults.StakeholderBenchmarkReport,
    bs = defaults.NetworkArrayOf(b),
    d = defaults.StakeholderBenchmarkDimensionReport,
    ds = defaults.NetworkPaginationSliceOf(d),
    dvs = defaults.NetworkPaginationSliceOf(defaults.StakeholderBenchmarkDimensionValue),
    bis = defaults.NetworkPaginationSliceOf(defaults.StakeholderBenchmarkCustomItem),
    bcs = defaults.StakeholderNewsPreviewArray,


    indicatorReportID = (indicatorID: number, filters: entities.FinancialIndicatorDataFilter): string =>
        `${indicatorID}_${filters.MarketScopeTypeID || 0}_${filters.BusinessID}`,

    resetIndicatorsDataReducer: IReducer<entities.FinancialIndicatorDataReport[], Action<'VOID_FINANCIAL_INDICATOR_DATA'>> = (state = [], action) => {
        switch (action.type) {
            case 'VOID_FINANCIAL_INDICATOR_DATA':
                if (action.payload.immediately === false) {
                    if (action.payload.indicatorID) {
                    // TODO it could also be more precise with period and corporate structure link,
                    // but there is no such stuff stored in data reports currently
                        return deepAssign(state, state.map(data => data.IndicatorID !== action.payload.indicatorID ? data : { Custom: { expires: new Date() } } as entities.FinancialIndicatorDataReport));
                    }

                    return state && deepAssign(state, state.map(_ => ({ Custom: { expires: new Date() } } as entities.FinancialIndicatorDataReport))) || [];
                }
                else {
                    if (action.payload.indicatorID) {
                    // TODO it could also be more precise with period and corporate structure link,
                    // but there is no such stuff stored in data reports currently
                        return state ? state.filter(data => data.IndicatorID !== action.payload.indicatorID) : [];
                    }

                    return [];
                }

            default:
                return state;
        }
    };

export default combineReducers({
    organizationClassification: entityLoadAllReducer(ws, 'LOAD_ALL_ORGANIZATION_CLASSIFICATION_TYPES'),
    organizationAutocomplete: switchReducers(wp,
        entityLoadAllReducer(wp, 'SEARCH_ORGANIZATION_AUTOCOMPLETE_SLICE'),
        (state: entities.NetworkPaginationSlice<entities.Entity> | undefined = wp, action: Action<'RESET_ORGANIZATION_AUTOCOMPLETE' | 'SEARCH_ORGANIZATION_AUTOCOMPLETE_SLICE'>): entities.NetworkPaginationSlice<entities.Entity> =>
            action.type === 'RESET_ORGANIZATION_AUTOCOMPLETE' || action.type === 'SEARCH_ORGANIZATION_AUTOCOMPLETE_SLICE' ?
                action.type !== 'SEARCH_ORGANIZATION_AUTOCOMPLETE_SLICE' || action.payload.status === RequestStatus.PENDING && !action.payload.startID ?
                    wp :
                    state || wp :
                state || defaultReducer(state, action)
    ),
    financialInformation: (
        state: { [key: string]: entities.NetworkObject<entities.FinancialsAndDecisionMakers>; } = {},
        action: Action<'LOAD_ORGANIZATION_FINANCIAL_INFORMATION' | 'DROP_ORGANIZATION_FINANCIAL_INFORMATION'>
    ) => {
        if (action.type === 'LOAD_ORGANIZATION_FINANCIAL_INFORMATION' || action.type === 'DROP_ORGANIZATION_FINANCIAL_INFORMATION') {
            return deepAssign(state, {
                [action.payload.financialInformationKey]: action.type === 'DROP_ORGANIZATION_FINANCIAL_INFORMATION' ?
                    Object.values(PersistentFinancialInformationKey).includes(action.payload.financialInformationKey) ?
                        defaults.NetworkObjectOf(defaults.FinancialsAndDecisionMakers) :
                        undefined :
                    networkObjectLoadReducer('LOAD_ORGANIZATION_FINANCIAL_INFORMATION', defaults.NetworkObjectOf(defaults.FinancialsAndDecisionMakers))(state[action.payload.financialInformationKey], action)
            });
        }

        return defaultReducer(state, action);
    },
    countries: entityLoadAllReducer(defaults.NetworkArrayOf(defaults.Country), 'LOAD_ALL_COUNTRIES'),
    marketScopeTypes: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.MarketScopeType), 'LOAD_ALL_MARKET_SCOPE_TYPES', MarketScopeType as any, 'MarketScopeType'),
    newsWatchFilterLevels: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.StakeholderNewsWatchFilterLevel), 'LOAD_ALL_STAKEHOLDER_NEWS_WATCH_FILTER_LEVELS', StakeholderNewsWatchFilterLevel as any, 'StakeholderNewsWatchFilterLevel'),
    stakeholderTypes: switchReducers(ts,
        entityLoadAllReducer(ts, 'LOAD_ALL_STAKEHOLDER_TYPES'),
        entityLoadReducer(ts, 'LOAD_STAKEHOLDER_TYPE'),
        entityAddReducer(ts, 'ADD_STAKEHOLDER_TYPE'),
        entityEditReducer(ts, 'EDIT_STAKEHOLDER_TYPE'),
        childrenReducer(ts,
            combineReducers(t, {
                Stakeholders: nestableEntitiesReducer(switchReducers(ss,
                    entityLoadAllReducer(ss, 'SEARCH_STAKEHOLDERS_SLICE'),
                    entityLoadReducer(ss, 'LOAD_STAKEHOLDER'),
                    entityAddReducer(ss, 'ADD_STAKEHOLDER'),
                    entityEditReducer(ss, 'EDIT_STAKEHOLDER'),
                    entityDeleteReducer(ss, 'DELETE_STAKEHOLDER'),
                    childrenReducer(ss,
                        combineReducers(s, {
                            Comments: switchReducers(cs,
                                entityLoadAllReducer(cs, 'SEARCH_STAKEHOLDER_COMMENTS_SLICE'),
                                entityAddReducer(cs, 'ADD_STAKEHOLDER_COMMENT')
                            )
                        }),
                        (state, action) => state.Data.getIndexByID(action.payload && (action.payload as any).parentID)
                    )
                ))
            }),
            (state, action) => state.Data.getIndexByID(action.payload && ((action.payload as any).grandParentID || (action.payload as any).parentID))
        ),
        (state: entities.NetworkArray<entities.StakeholderTypeReport> = ts, action: Action<'DELETE_MARKET'>) => {
            switch (action.type) {
                case 'DELETE_MARKET':
                    return action.payload.status === RequestStatus.SUCCEEDED ?
                        deepAssign(state, {
                            Data: state.Data.map(stakeholderType => ({
                                ...stakeholderType,
                                Stakeholders: {
                                    ...stakeholderType.Stakeholders,
                                    Data: stakeholderType.Stakeholders.Data.map(stakeholder => ({
                                        ...stakeholder,
                                        MarketID: stakeholder.MarketID === action.payload.entityID ? defaults.StakeholderReport.MarketID : stakeholder.MarketID
                                    }))
                                }
                            }))
                        } as any) :
                        state;

                default:
                    return state;
            }
        }
    ),
    stakeholderNewsPreview: switchReducers(bcs,
        entityLoadAllReducer(bcs, 'LOAD_STAKEHOLDER_NEWS_PREVIEW'),
        (state: entities.StakeholderNewsPreviewArray | undefined = bcs, action: Action<'LOAD_STAKEHOLDER_NEWS_PREVIEW' | 'DROP_STAKEHOLDER_NEWS_PREVIEW'>) => {
            switch (action.type) {
                case 'LOAD_STAKEHOLDER_NEWS_PREVIEW':
                    return action.payload.status === RequestStatus.PENDING ?
                        deepAssign(state, {
                            Custom: {
                                previewSettings: {
                                    brandNames: action.payload.brandNames,
                                    includeEnglishMarkets: action.payload.includeEnglishMarkets,
                                    newsSearch: action.payload.newsSearch,
                                    typeID: action.payload.typeID,
                                    stakeholderID: action.payload.stakeholderID,
                                    strictnessLevel: action.payload.strictnessLevel,
                                    webSearch: action.payload.webSearch
                                }
                            }
                        } as any) :
                        state;

                case 'DROP_STAKEHOLDER_NEWS_PREVIEW':
                    return bcs;

                default:
                    return defaultReducer(state, action);
            }
        }
    ),

    financialIndicators: switchReducers(is,
        entityLoadAllReducer(is, 'LOAD_ALL_FINANCIAL_INDICATORS'),
        entityVoidAllReducer(is, 'VOID_ALL_FINANCIAL_INDICATORS')
    ),
    financialIndicatorsData: switchReducers([] as entities.FinancialIndicatorDataReport[],
        resetIndicatorsDataReducer,
        (state: entities.FinancialIndicatorDataReport[] | undefined = [], action: Action<'SEARCH_FINANCIAL_INDICATOR_DATA'>): entities.FinancialIndicatorDataReport[] => {
            switch (action.type) {
                case 'SEARCH_FINANCIAL_INDICATOR_DATA':
                    switch (action.payload.status) {
                        case RequestStatus.PENDING:
                        case RequestStatus.SUCCEEDED:
                        case RequestStatus.FAILED:
                            return deepAssign(
                                state,
                                ((action.payload.status === RequestStatus.SUCCEEDED ? action.payload.response : action.payload.templates) as entities.FinancialIndicatorDataReport[])
                                    .reduce((state, newData) => {
                                        newData = { ...defaults.FinancialIndicatorDataReport, ...newData, ID: newData.ID };
                                        newData = {
                                            ...newData,
                                            ID: newData.ID || indicatorReportID(newData.IndicatorID, {
                                                MarketScopeTypeID: newData.MarketScopeTypeID || null,
                                                BusinessID: newData.BusinessID || ''
                                            })
                                        };
                                        let oldData = state.find(({ ID }) => ID === newData.ID);
                                        if (oldData && newData) {
                                            if (action.payload.status === RequestStatus.SUCCEEDED) {
                                                newData = {
                                                    ...newData,
                                                    Data: [
                                                        ...oldData.Data.filter(item => newData.StartYear && newData.StartYear >= item.Year || newData.EndYear && newData.EndYear <= item.Year),
                                                        ...newData.Data
                                                    ].sortBy('Year')
                                                };
                                            }
                                            else {
                                                newData = {
                                                    ...newData,
                                                    Data: oldData.Data
                                                };
                                            }

                                            const
                                                startYear: number | null = !oldData.StartYear || !newData.StartYear ? null : oldData.StartYear < newData.StartYear ? oldData.StartYear : newData.StartYear || null,
                                                endYear: number | null = !oldData.EndYear || !newData.EndYear ? null : oldData.EndYear > newData.EndYear ? oldData.EndYear : newData.EndYear || null;

                                            let loadedRanges: {
                                                start: number | null;
                                                end: number | null;
                                                status: RequestStatus;
                                            }[] = deepClone(oldData.Custom.loadedRanges);

                                            if (!loadedRanges || !loadedRanges.length || !startYear && !endYear) {
                                                loadedRanges = [{ start: startYear, end: endYear, status: action.payload.status === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED : RequestStatus.PENDING }];
                                            }
                                            else {
                                                // TODO optimize this stuff
                                                loadedRanges = loadedRanges
                                                    .map((range, index, ranges) => [
                                                        {
                                                            year: range.start || -Infinity,
                                                            left: ranges[index - 1] && (ranges[index - 1].end || Infinity) >= (range.start || -Infinity) ? ranges[index - 1].status : RequestStatus.UNSYNCED,
                                                            right: range.status
                                                        },
                                                        {
                                                            year: range.end || Infinity,
                                                            left: range.status,
                                                            right: ranges[index + 1] && (ranges[index + 1].start || -Infinity) <= (range.end || -Infinity) ? ranges[index + 1].status : RequestStatus.UNSYNCED
                                                        }
                                                    ])
                                                    .flatten<{ year: number; left: RequestStatus; right: RequestStatus; }>()
                                                    .concat([
                                                        {
                                                            year: newData.StartYear || -Infinity,
                                                            left: RequestStatus.UNSYNCED,
                                                            right: action.payload.status === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED : RequestStatus.PENDING
                                                        },
                                                        {
                                                            year: newData.EndYear || Infinity,
                                                            left: action.payload.status === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED : RequestStatus.PENDING,
                                                            right: RequestStatus.UNSYNCED
                                                        }
                                                    ])
                                                    .sortBy('year')
                                                    .groupBy('year')
                                                    .map(group => ({
                                                        year: group[0].year,
                                                        left: group.reduce((r, { left }) => r === RequestStatus.SUCCEEDED || left === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED :
                                                        r === RequestStatus.PENDING || left === RequestStatus.PENDING ? RequestStatus.PENDING :
                                                        RequestStatus.UNSYNCED, group[0].left),
                                                        right: group.reduce((r, { right }) => r === RequestStatus.SUCCEEDED || right === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED :
                                                        r === RequestStatus.PENDING || right === RequestStatus.PENDING ? RequestStatus.PENDING :
                                                        RequestStatus.UNSYNCED, group[0].right)
                                                    }))
                                                    .map((point, index, points) => ({
                                                        start: point.year,
                                                        status: point.right === RequestStatus.SUCCEEDED || (points[index + 1] ? points[index + 1].left : point.right) === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED :
                                                        point.right === RequestStatus.PENDING || (points[index + 1] ? points[index + 1].left : point.right) === RequestStatus.PENDING ? RequestStatus.PENDING :
                                                        RequestStatus.UNSYNCED,
                                                        end: points[index + 1] ? points[index + 1].year : point.year
                                                    }))
                                                    .slice(0, -1)
                                                    .filter(range => range.start < range.end && range.status !== RequestStatus.UNSYNCED);
                                                for (let i = 0; i < loadedRanges.length; i++) {
                                                    if (loadedRanges[i] && loadedRanges[i + 1] && loadedRanges[i].status === loadedRanges[i + 1].status && (loadedRanges[i].end || Infinity) >= (loadedRanges[i + 1].start || -Infinity)) {
                                                        loadedRanges[i].end = loadedRanges[i + 1].end;
                                                        loadedRanges.splice(i + 1, 1);
                                                        i--;
                                                    }
                                                }
                                            }

                                            newData = {
                                                ...newData,
                                                StartYear: startYear,
                                                EndYear: endYear,
                                                Custom: {
                                                    ...newData.Custom,
                                                    loadedRanges
                                                }
                                            };

                                            oldData = deepAssign(oldData, { Custom: { loadedRanges } });
                                        }
                                        else if (!newData.Custom.loadedRanges.length) {
                                            newData = {
                                                ...newData,
                                                Custom: {
                                                    ...newData.Custom,
                                                    loadedRanges: [{ start: newData.StartYear, end: newData.EndYear, status: action.payload.status === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED : RequestStatus.PENDING }]
                                                }
                                            };
                                        }


                                        return [
                                            ...state.filter(({ ID }) => ID !== newData.ID),
                                            entityLoadReducer(defaults.FinancialIndicatorDataReport, 'SEARCH_FINANCIAL_INDICATOR_DATA')
                                            ({ Data: oldData || newData ? [oldData || newData] : [] } as any,
                                                action.payload.status === RequestStatus.SUCCEEDED ?
                                                    { ...action, payload: { ...action.payload, entityID: newData.ID as unknown as number, response: newData } } as any :
                                                    { ...action, payload: { ...action.payload, entityID: newData.ID as unknown as number, response: newData } } as any
                                            ).Data[0] as any
                                        ];
                                    }, state)
                                    .sortBy('ID')
                            );

                        default:
                            return state;
                    }

                default:
                    return state;
            }
        }
    ),
    stakeholderMarketPositioningMapViewTemplates: entityLoadAllReducer(vts, 'LOAD_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_TEMPLATES'),
    stakeholderMarketPositioningMapViews: sortedEntitiesReducer(vs, 'Name', switchReducers(vs,
        entityLoadAllReducer(vs, 'LOAD_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEWES'),
        entityLoadReducer(vs, 'LOAD_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW'),
        entityAddReducer(vs, 'ADD_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW'),
        entityEditReducer(vs, 'EDIT_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW'),
        childrenReducer(vs,
            combineReducers(v, {
                Positions: nestableEntitiesReducer(switchReducers(ps,
                    entityLoadAllReducer(ps, 'LOAD_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS'),
                    entityLoadReducer(ps, 'LOAD_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITION'),
                    entityAddAllReducer(ps, 'ADD_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS'),
                    entityAddReducer(ps, 'ADD_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITION'),
                    entityEditAllReducer(ps, 'EDIT_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS'),
                    entityEditReducer(ps, 'EDIT_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITION'),
                    entityDeleteAllReducer(ps, 'DELETE_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS'),
                    entityDeleteReducer(ps, 'DELETE_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITION'),
                    entityVoidAllReducer(ps, 'VOID_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS')
                )),
                Items: switchReducers(vis,
                    entityLoadAllReducer(vis, 'SEARCH_MARKET_POSITIONING_MAP_VIEW_CUSTOM_ITEMS_SLICE'),
                    entityAddAllReducer(vis, 'ADD_ALL_MARKET_POSITIONING_MAP_VIEW_CUSTOM_ITEMS'),
                    entityEditAllReducer(vis, 'EDIT_ALL_MARKET_POSITIONING_MAP_VIEW_CUSTOM_ITEMS'),
                    entityDeleteAllReducer(vis, 'DELETE_ALL_MARKET_POSITIONING_MAP_VIEW_CUSTOM_ITEMS')
                ),
                Custom: (state = v.Custom, action: Action<'INVITE_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_USERS'>) =>
                    action.type === 'INVITE_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_USERS' ?
                        deepAssign(state, { invitationStatus: action.payload.status }) :
                        state
            }),
            (state, action) => state.Data.getIndexByID(action.payload && ((action.payload as any).grandParentID || (action.payload as any).parentID || (action.payload as any).entityID))
        ),
        entityDeleteReducer(vs, 'DELETE_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW'),
        entityVoidAllReducer(vs, 'VOID_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEWS')
    )
    ),
    stakeholderBenchmarkTemplates: entityLoadAllReducer(bts, 'LOAD_ALL_STAKEHOLDER_BENCHMARK_TEMPLATES'),
    stakeholderBenchmarks: sortedEntitiesReducer(bs, 'Name', switchReducers(bs,
        entityLoadAllReducer(bs, 'LOAD_ALL_STAKEHOLDER_BENCHMARKES'),
        entityLoadReducer(bs, 'LOAD_STAKEHOLDER_BENCHMARK'),
        entityAddReducer(bs, 'ADD_STAKEHOLDER_BENCHMARK'),
        entityEditReducer(bs, 'EDIT_STAKEHOLDER_BENCHMARK'),
        childrenReducer(bs,
            combineReducers(b, {
                Dimensions: nestableEntitiesReducer(switchReducers(ds,
                    entityLoadAllReducer(ds, 'LOAD_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS'),
                    entityLoadReducer(ds, 'LOAD_STAKEHOLDER_BENCHMARK_DIMENSION'),
                    entityAddAllReducer(ds, 'ADD_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS'),
                    entityAddReducer(ds, 'ADD_STAKEHOLDER_BENCHMARK_DIMENSION'),
                    entityEditAllReducer(ds, 'EDIT_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS'),
                    entityEditReducer(ds, 'EDIT_STAKEHOLDER_BENCHMARK_DIMENSION'),
                    childrenReducer(ds,
                        combineReducers(d, {
                            Values: nestableEntitiesReducer(switchReducers(dvs,
                                entityLoadAllReducer(dvs, 'LOAD_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES'),
                                entityLoadReducer(dvs, 'LOAD_STAKEHOLDER_BENCHMARK_DIMENSION_VALUE'),
                                entityAddAllReducer(dvs, 'ADD_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES'),
                                entityAddReducer(dvs, 'ADD_STAKEHOLDER_BENCHMARK_DIMENSION_VALUE'),
                                entityEditAllReducer(dvs, 'EDIT_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES'),
                                entityEditReducer(dvs, 'EDIT_STAKEHOLDER_BENCHMARK_DIMENSION_VALUE'),
                                entityDeleteAllReducer(dvs, 'DELETE_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES'),
                                entityDeleteReducer(dvs, 'DELETE_STAKEHOLDER_BENCHMARK_DIMENSION_VALUE'),
                                entityVoidAllReducer(dvs, 'VOID_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES')
                            ))
                        }),
                        (state, action) => state.Data.getIndexByID(action.payload && ((action.payload as any).parentID || (action.payload as any).entityID))
                    ),
                    entityDeleteAllReducer(ds, 'DELETE_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS'),
                    entityDeleteReducer(ds, 'DELETE_STAKEHOLDER_BENCHMARK_DIMENSION'),
                    entityVoidAllReducer(ds, 'VOID_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS')
                )),
                Items: switchReducers(bis,
                    entityLoadAllReducer(bis, 'SEARCH_BENCHMARK_CUSTOM_ITEMS_SLICE'),
                    entityAddAllReducer(bis, 'ADD_ALL_BENCHMARK_CUSTOM_ITEMS'),
                    entityEditAllReducer(bis, 'EDIT_ALL_BENCHMARK_CUSTOM_ITEMS'),
                    entityDeleteAllReducer(bis, 'DELETE_ALL_BENCHMARK_CUSTOM_ITEMS')
                ),
                Custom: (state = b.Custom, action: Action<'INVITE_STAKEHOLDER_BENCHMARK_USERS'>) =>
                    action.type === 'INVITE_STAKEHOLDER_BENCHMARK_USERS' ?
                        deepAssign(state, { invitationStatus: action.payload.status }) :
                        state
            }),
            (state, action) => state.Data.getIndexByID(action.payload && ((action.payload as any).grandParentID || (action.payload as any).parentID || (action.payload as any).entityID))
        ),
        entityDeleteReducer(bs, 'DELETE_STAKEHOLDER_BENCHMARK'),
        entityVoidAllReducer(bs, 'VOID_ALL_STAKEHOLDER_BENCHMARKS')
    )
    )
});


